import { ChangeEvent, useCallback, useState } from 'react';
import { ColumnBoxFlexFullWidth, FlexBoxFullWidth } from '../common/boxes';
import { Button, TextField } from '@mui/material';
import { TEXTS } from '../../core/texts';
import { ValidationErrorState } from '../../types/validationErrorState';
import { Modal } from '../common/Modal';
import { Validator } from '../../core/Validator';
import { ButtonWithLoader } from '../common/ButtonWithLoader';
import { useMutation } from '@tanstack/react-query';
import { AdminAuthService } from '../../services/admin-auth';
import { useNotification } from '../../+state/useNotification';
import { AxiosError } from 'axios';
import { IErrorResponse } from '../../interfaces/errorResponse';
import { Lock } from '@mui/icons-material';

const initialPasswords = { oldPassword: '', password: '', rePassword: '' };

const ChangeUserPassword = () => {
  const [openedModal, setOpenedModel] = useState(false);
  const [passwords, setPasswords] = useState(initialPasswords);
  const [validationErrors, setValidationErrors] = useState<ValidationErrorState>({});
  const { openErrorToast, openSuccessToast } = useNotification();

  const toggleModal = useCallback((to?: boolean) => {
    setOpenedModel(prev => to !== undefined ? to : !prev);
    setPasswords(initialPasswords);
    setValidationErrors({});
  }, []);

  const mutation = useMutation({
    mutationFn: ({ oldPassword, newPassword }: { oldPassword: string, newPassword: string }) =>
      AdminAuthService.changePassword(oldPassword, newPassword),
    onSuccess: () => {
      openSuccessToast(TEXTS.SUCCESS_PASSWORD_CHANGE);
      toggleModal(false)
    },
    onError: (err: AxiosError<IErrorResponse>) => {
      const msg = err.response?.data.message || err.message;
      openErrorToast(msg);
      const validError: ValidationErrorState = msg === 'Old password is incorrect'
        ? { oldPassword: msg } : { password: msg.includes('Validation') ? TEXTS.PASSWORD_ERROR : msg };
      setValidationErrors(validError);
    }
  });

  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswords(prev => ({ ...prev, [name]: value }));
  }, []);
  

  const handleSubmit = useCallback((e: ChangeEvent<HTMLFormElement>) => {
    e.preventDefault();
    const validErrors = Validator.validate(passwords, Validator.NAMES.AUTH);
    if (validErrors) {
      return setValidationErrors(validErrors);
    }
    setValidationErrors({});
    mutation.mutate({ oldPassword: passwords.oldPassword, newPassword: passwords.password });
  }, [passwords, mutation]);

  return (
    <FlexBoxFullWidth justifyContent={'space-between'} alignItems={'center'}>
      <Button sx={{ minWidth: 'fit-content' }} variant={'outlined'} onClick={() => toggleModal()}>
        {TEXTS.CHANGE_PASSWORD}
      </Button>
      {
        openedModal &&
        <Modal
          onClose={() => toggleModal()} maxWidth={500} withDividers={false} title={TEXTS.CHANGE_PASSWORD}
          headerIcon={<Lock color={'primary'}/>}
        >
          <form onSubmit={handleSubmit}>

            <ColumnBoxFlexFullWidth p={1} pt={2}>

              <TextField name={'oldPassword'} label={TEXTS.OLD_PASSWORD} type={'password'} onChange={handleInputChange}
                         error={!!validationErrors['oldPassword']} helperText={validationErrors['oldPassword']}
                         value={passwords.oldPassword} autoComplete={'current-password'}
              />
              <TextField name={'password'} label={TEXTS.NEW_PASSWORD} type={'password'} onChange={handleInputChange}
                         error={!!validationErrors['password']} helperText={validationErrors['password']}
                         value={passwords.password} sx={{ my: 2 }} autoComplete={'new-password'}
              />
              <TextField name={'rePassword'} label={TEXTS.REPEAT_NEW_PASSWORD} type={'password'}
                         onChange={handleInputChange}
                         error={!!validationErrors['rePassword']} helperText={validationErrors['rePassword']}
                         value={passwords.rePassword} autoComplete={'new-password'}
              />
              <FlexBoxFullWidth justifyContent={'space-between'} mt={3}>
                <Button onClick={() => toggleModal()} color={'error'} variant={'outlined'}>{TEXTS.CANCEL}</Button>
                <ButtonWithLoader loading={mutation.isLoading} variant={'outlined'}
                                  type={'submit'}>{TEXTS.SAVE}</ButtonWithLoader>
              </FlexBoxFullWidth>
            </ColumnBoxFlexFullWidth>
          </form>
        </Modal>

      }
    </FlexBoxFullWidth>
  );
};

export default ChangeUserPassword;